/**
 * The one place that answers "how long does this keep?".
 *
 * Before this existed the app had four estimators that disagreed: a coarse map
 * in the delivery import, a category table in pantryFreshnessService, an inline
 * table in inventorySlice, and whatever Gemini said in the photo and receipt
 * prompts. A whole chicken got fourteen days from one and two from another, so
 * the number you saw depended on how the food got into the pantry.
 *
 * Resolution order, first hit wins:
 *   1. the name table  (src/data/shelfLife.ts) — specific, reviewed, offline
 *   2. the model hint  (a bucket from the photo/receipt/email prompt)
 *   3. the category default — the last resort, and deliberately pessimistic
 *
 * Then the visible condition shortens the answer, because a photo showing
 * wilting greens beats anything a table knows.
 */
import { SHELF_LIFE_TABLE, ShelfLifeEntry } from '../data/shelfLife';
import { PantryLocation } from '../utils/pantryLocation';
import { PantryExpiryDate } from '../utils/pantryExpiry';
import { ShelfLifeBucket, VisibleCondition, toShelfLifeBucket } from '../types/foodRecognition';

export type ShelfLifeSource = 'table' | 'model' | 'category' | 'user';

export interface ShelfLifeQuery {
  name: string;
  category: string;
  location: PantryLocation;
  /** Opened, cut, or otherwise no longer sealed. */
  opened?: boolean;
  /** From a photo scan — 'wilting' and 'near-expiry' override the estimate downward. */
  condition?: VisibleCondition;
  /** What the model said, used only when the table has no row for this name. */
  hint?: ShelfLifeBucket;
}

export interface ShelfLifeResult {
  /** Days from today, or null for food that needs no expiry pill at all. */
  days: number | null;
  source: ShelfLifeSource;
  confidence: number;
  explanation: string;
  /** Which table row answered, for analytics and for the eval harness. */
  matchedKey?: string;
}

/** Buckets are what a model can reliably produce; days are what the pantry needs. */
const BUCKET_DAYS: Record<ShelfLifeBucket, number | null> = {
  'highly-perishable': 2,
  perishable: 5,
  'week-plus': 12,
  'keeps-weeks': 35,
  'shelf-stable': null,
};

/**
 * Category fallback, per zone. Reached only when neither the table nor the
 * model has an opinion, so it errs short: a pantry that nags early is annoying,
 * one that nags late is useless.
 */
const CATEGORY_DAYS: Record<string, Partial<Record<PantryLocation, number>>> = {
  Meat: { fridge: 3, counter: 1, pantry: 3 },
  'Plant-Based Protein': { fridge: 7, pantry: 30, counter: 7 },
  Vegetables: { fridge: 7, counter: 5, pantry: 14 },
  Fruits: { fridge: 7, counter: 5, pantry: 14 },
  Dairy: { fridge: 10, counter: 1, pantry: 10 },
  'Bread/Grains': { counter: 4, fridge: 10, pantry: 7 },
  Leftovers: { fridge: 3, counter: 1, pantry: 1 },
  Beverages: { fridge: 14, pantry: 90, counter: 30 },
};

/** Freezing stops spoilage; these are quality horizons, not safety ones. */
const CATEGORY_FREEZER_DAYS: Record<string, number> = {
  Meat: 180,
  'Plant-Based Protein': 150,
  Vegetables: 300,
  Fruits: 300,
  Dairy: 90,
  'Bread/Grains': 90,
  Leftovers: 90,
  Beverages: 90,
};
const DEFAULT_FREEZER_DAYS = 180;

/** Categories that get no expiry pill unless the table says otherwise. */
export const SHELF_STABLE_CATEGORIES = ['Condiments', 'Spices', 'Packaged Foods', 'Other'];

/**
 * How long an opened item lasts when the table has no `openedDays` row.
 * Opening starts a clock that the sealed number knows nothing about.
 */
const OPENED_CAP: Record<string, number> = {
  Meat: 3,
  'Plant-Based Protein': 4,
  Dairy: 7,
  Leftovers: 3,
  Beverages: 7,
  Vegetables: 4,
  Fruits: 4,
  'Bread/Grains': 5,
};
const DEFAULT_OPENED_CAP = 7;

/** Accent-fold and flatten punctuation so "Doré haché" and "dore hache" compare equal. */
export function foldName(name: string): string {
  return (name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['\u2019]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Word-boundary containment, tolerant of a plural on the last word.
 *
 * Both English plural shapes are needed: "onions" off "onion", and
 * "strawberries" off "strawberry" — the y/ies pair is not decoration, it is
 * most of the berry aisle, and without it those rows were invisible.
 */
function containsAlias(haystack: string, alias: string): boolean {
  const escaped = alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const plural = alias.endsWith('y')
    ? `(?:${escaped}|${escaped.slice(0, -1)}ies)`
    : `${escaped}(?:e?s)?`;
  return new RegExp(`(^| )${plural}( |$)`).test(haystack);
}

/**
 * The table row for a name, or null.
 *
 * Matched against both the raw folded name and, when supplied, a normalised
 * variant — `normalizeIngredientName` translates French descriptors but not
 * French nouns, so "poulet entier" survives only on the raw string while
 * "2 lb organic carrots" survives only on the normalised one.
 *
 * Longest alias wins, counted in words then characters, so "ground chicken"
 * beats "chicken" and never the other way round — except that a `productNoun`
 * row outranks every ingredient row, so "strawberry jam" is jam.
 */
export function matchShelfLifeEntry(name: string, normalized?: string): ShelfLifeEntry | null {
  const candidates = [foldName(name), foldName(normalized || '')].filter(Boolean);
  if (candidates.length === 0) return null;

  let best: ShelfLifeEntry | null = null;
  let bestScore = -1;

  for (const entry of SHELF_LIFE_TABLE) {
    for (const alias of entry.match) {
      if (!candidates.some((c) => containsAlias(c, alias))) continue;
      // Ranked: how many words the alias matched, then whether the row names a
      // made product, then its length.
      //
      // Word count has to come first so an explicit alias is always the way out.
      // 'bread' is a product and 'flour' is not, but "bread flour" is flour, and
      // the only reliable fix for a case like that is to spell it out — which
      // must then beat the product flag.
      //
      // Position in the string is deliberately not used. English puts the head
      // noun last ("...cheese focaccia") and French puts it first ("tomate coeur
      // de boeuf"), so ranking by position gets one of the two languages wrong.
      const score =
        alias.split(' ').length * 1_000_000 + (entry.productNoun ? 10_000 : 0) + alias.length;
      if (score > bestScore) {
        bestScore = score;
        best = entry;
      }
    }
  }
  return best;
}

/** Pick the day count for a zone, falling back along a documented chain. */
function daysForZone(
  days: Partial<Record<PantryLocation, number>> | undefined,
  location: PantryLocation
): number | undefined {
  if (!days) return undefined;
  if (days[location] !== undefined) return days[location];
  // A missing zone is answered by the nearest colder-or-similar one rather than
  // by an average, so a counter-only row never claims to know the fridge.
  const chain: Record<PantryLocation, PantryLocation[]> = {
    fridge: ['counter', 'pantry'],
    counter: ['pantry', 'fridge'],
    pantry: ['counter', 'fridge'],
    freezer: ['fridge', 'counter', 'pantry'],
  };
  for (const zone of chain[location]) {
    if (days[zone] !== undefined) return days[zone];
  }
  return undefined;
}

function applyCondition(days: number, condition?: VisibleCondition): number {
  if (condition === 'near-expiry') return 1;
  if (condition === 'wilting' || condition === 'slightly soft') return Math.max(1, Math.floor(days / 2));
  return days;
}

export function estimateShelfLife(q: ShelfLifeQuery, normalizedName?: string): ShelfLifeResult {
  const { name, category, location, opened, condition, hint } = q;

  // 1 ── the table
  const entry = matchShelfLifeEntry(name, normalizedName);
  if (entry) {
    const result = resolveEntry(entry, location, opened, category);
    if (result.days === null) {
      return { ...result, source: 'table', confidence: 90, matchedKey: entry.key, explanation: `${entry.key} keeps indefinitely` };
    }
    return {
      days: applyCondition(result.days, condition),
      source: 'table',
      confidence: 90,
      matchedKey: entry.key,
      explanation: `${entry.key}, ${opened ? 'opened' : 'sealed'}, in the ${location}`,
    };
  }

  // 2 ── the model's bucket
  // Re-validated here as well as at each caller: this is the last place a bad
  // string can be turned into a date, and a date is what gets persisted.
  const bucket = toShelfLifeBucket(hint);
  if (bucket) {
    const bucketDays = BUCKET_DAYS[bucket];
    if (bucketDays === null) {
      return { days: null, source: 'model', confidence: 70, explanation: 'Model called it shelf-stable' };
    }
    const days = location === 'freezer'
      ? CATEGORY_FREEZER_DAYS[category] ?? DEFAULT_FREEZER_DAYS
      : capIfOpened(bucketDays, opened, category);
    return {
      days: applyCondition(days, condition),
      source: 'model',
      confidence: 70,
      explanation: `Model estimate (${bucket}) in the ${location}`,
    };
  }

  // 3 ── the category
  // Nothing in the cupboard gets a guessed date. A table row for potatoes may
  // still date them; a category guess about "Meat in the pantry" may not.
  if (location === 'pantry') {
    return { days: null, source: 'category', confidence: 50, explanation: `${category} in the pantry keeps indefinitely` };
  }
  if (location === 'freezer') {
    return {
      days: CATEGORY_FREEZER_DAYS[category] ?? DEFAULT_FREEZER_DAYS,
      source: 'category',
      confidence: 50,
      explanation: `Frozen ${category}`,
    };
  }
  const fromCategory = daysForZone(CATEGORY_DAYS[category], location);
  if (fromCategory === undefined) {
    return { days: null, source: 'category', confidence: 40, explanation: `No shelf life known for ${category}` };
  }
  return {
    days: applyCondition(capIfOpened(fromCategory, opened, category), condition),
    source: 'category',
    confidence: 50,
    explanation: `Estimated from category (${category}) and storage (${location})`,
  };
}

function capIfOpened(days: number, opened: boolean | undefined, category: string): number {
  if (!opened) return days;
  return Math.min(days, OPENED_CAP[category] ?? DEFAULT_OPENED_CAP);
}

function resolveEntry(
  entry: ShelfLifeEntry,
  location: PantryLocation,
  opened: boolean | undefined,
  category: string
): { days: number | null } {
  // The freezer overrides everything except an explicit frozen row: a sealed
  // tofu's 45 fridge days say nothing about how it freezes.
  if (location === 'freezer') {
    const frozen = entry.days?.freezer ?? CATEGORY_FREEZER_DAYS[category] ?? DEFAULT_FREEZER_DAYS;
    return { days: frozen };
  }

  if (opened) {
    const openedDays = daysForZone(entry.openedDays, location);
    if (openedDays !== undefined) return { days: openedDays };
    // An opened shelf-stable jar with no opened row stays shelf-stable —
    // opening a bag of sugar does not start a clock.
    if (entry.shelfStable) return { days: null };
    const sealed = daysForZone(entry.days, location);
    return { days: sealed === undefined ? null : capIfOpened(sealed, true, category) };
  }

  if (entry.shelfStable) return { days: null };
  const sealed = daysForZone(entry.days, location);
  return { days: sealed === undefined ? null : sealed };
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * The expiry date a pantry row should carry, or undefined when the food needs
 * no pill. `source` is stamped so a later re-estimate knows not to overwrite a
 * date the user set by hand.
 */
export function shelfLifeExpiryDate(
  q: ShelfLifeQuery,
  today: Date = new Date(),
  normalizedName?: string
): PantryExpiryDate | undefined {
  const result = estimateShelfLife(q, normalizedName);
  if (result.days === null) return undefined;

  // Calendar arithmetic, not milliseconds: a 180-day horizon that crosses a
  // clock change loses an hour, and daysUntilExpiry rounds that into a lost day.
  const expiry = startOfDay(today);
  expiry.setDate(expiry.getDate() + result.days);
  // matchedKey is spread in only when there is one: these objects are written
  // straight to Firestore, which rejects an explicit undefined.
  return {
    day: expiry.getDate(),
    month: expiry.getMonth() + 1,
    year: expiry.getFullYear(),
    confidence: result.confidence,
    explanation: result.explanation,
    source: result.source,
    ...(result.matchedKey ? { matchedKey: result.matchedKey } : {}),
  };
}
